import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import './StaffHRApplicationsPage.css';

const statusOptions = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'reviewed', label: 'In Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'needsMoreInfo', label: 'Needs More Info' },
  { value: 'waitlisted', label: 'Waitlisted' },
  { value: 'archived', label: 'Archived' }
];

const statusLabel = (status) => {
  const match = statusOptions.find((option) => option.value === status);
  return match ? match.label : status;
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleDateString();
};

const isPotentialFileValue = (value) => {
  if (!value || typeof value !== 'string') return false;
  return value.startsWith('/uploads/') || value.startsWith('http://') || value.startsWith('https://');
};

const parseFilenameFromContentDisposition = (contentDisposition) => {
  if (!contentDisposition || typeof contentDisposition !== 'string') return null;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] || null;
};

const getExtensionFromValue = (value = '') => {
  if (!value || typeof value !== 'string') return '';
  const clean = value.split('?')[0].split('#')[0];
  const match = clean.match(/\.([a-z0-9]{2,5})$/i);
  return match ? `.${match[1].toLowerCase()}` : '';
};

const getExtensionFromMimeType = (mimeType = '') => {
  const normalized = String(mimeType || '').toLowerCase();
  if (normalized.includes('pdf')) return '.pdf';
  if (normalized.includes('msword')) return '.doc';
  if (normalized.includes('officedocument.wordprocessingml.document')) return '.docx';
  if (normalized.includes('png')) return '.png';
  if (normalized.includes('jpeg') || normalized.includes('jpg')) return '.jpg';
  if (normalized.includes('gif')) return '.gif';
  if (normalized.includes('webp')) return '.webp';
  if (normalized.includes('text/plain')) return '.txt';
  return '';
};

const looksLikeStorageFilename = (filename = '') => {
  if (!filename) return false;
  const lower = filename.toLowerCase();
  return /^\d{10,}-\d+\.[a-z0-9]{2,5}$/i.test(lower)
    || /^[a-f0-9-]{20,}\.[a-z0-9]{2,5}$/i.test(lower)
    || /_[0-9]{10,}\.[a-z0-9]{2,5}$/i.test(lower);
};

const sanitizeFilename = (value = '') => {
  const clean = String(value || '').replace(/[<>:"/\\|?*\x00-\x1F]/g, ' ').replace(/\s+/g, ' ').trim();
  return clean || 'resume';
};

const resolvePreferredResumeFilename = (application, contentDisposition) => {
  const headerName = parseFilenameFromContentDisposition(contentDisposition);
  const candidateName = application?.resumeFilename || headerName || '';
  const candidateExt = getExtensionFromValue(candidateName)
    || getExtensionFromValue(application?.resumeUrl)
    || getExtensionFromMimeType(application?.resumeFileType)
    || '.pdf';

  if (application?.resumeFilename && !looksLikeStorageFilename(application.resumeFilename)) {
    const clean = sanitizeFilename(application.resumeFilename);
    return getExtensionFromValue(clean) ? clean : `${clean}${candidateExt}`;
  }

  const baseFromApplicant = sanitizeFilename(application?.applicantName || `resume-${application?._id || 'file'}`);
  return `${baseFromApplicant}${candidateExt}`;
};

const StaffHRApplicationsPage = () => {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewingApplication, setViewingApplication] = useState(null);

  const [selectedJobId, setSelectedJobId] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [downloadingGroup, setDownloadingGroup] = useState(null);
  const [reviewNotesDraft, setReviewNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesError, setNotesError] = useState('');
  const [focusNotesForApplicationId, setFocusNotesForApplicationId] = useState(null);
  const reviewNotesRef = useRef(null);
  const downloadingResumeIdsRef = useRef(new Set());

  const canAccess = Boolean(currentUser && currentUser.hr);

  const getFileUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${api.defaults.baseURL}${url}`;
  };

  const fetchResumeBlob = async (applicationId, inline = false) => {
    const response = await api.get(`/api/hr/applications/${applicationId}/resume`, {
      responseType: 'blob',
      params: inline ? { inline: 'true' } : undefined
    });

    return {
      blob: response.data,
      contentDisposition: response.headers['content-disposition']
    };
  };

  const triggerBlobDownload = (blob, filename) => {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const openResumeInNewTab = async (application) => {
    const previewWindow = window.open('', '_blank', 'noopener,noreferrer');
    try {
      const { blob } = await fetchResumeBlob(application._id, true);
      const objectUrl = URL.createObjectURL(blob);
      if (previewWindow && !previewWindow.closed) {
        previewWindow.location.href = objectUrl;
      } else {
        triggerBlobDownload(blob, resolvePreferredResumeFilename(application));
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err) {
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
      console.error('[HR] Open resume failed:', err);
      alert(err.response?.data?.message || 'Unable to open resume. The file may not be available on this server.');
    }
  };

  const downloadResume = async (application) => {
    const appId = String(application?._id || '');
    if (!appId || downloadingResumeIdsRef.current.has(appId)) {
      return false;
    }

    downloadingResumeIdsRef.current.add(appId);
    try {
      const { blob, contentDisposition } = await fetchResumeBlob(application._id, false);
      const filename = resolvePreferredResumeFilename(application, contentDisposition);

      triggerBlobDownload(blob, filename);
      return true;
    } catch (err) {
      console.error('[HR] Download resume failed:', err);
      return false;
    } finally {
      downloadingResumeIdsRef.current.delete(appId);
    }
  };

  const exportGroupJson = (group) => {
    const safeTitle = (group.jobTitle || 'applicants').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const blob = new Blob([JSON.stringify(group.items, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${safeTitle}-applicants.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const downloadAllResumes = async (groupJobId, items) => {
    setDownloadingGroup(groupJobId);
    const withResumes = items.filter((a) => a.resumeUrl);
    const failedNames = [];
    for (const app of withResumes) {
      const ok = await downloadResume(app);
      if (!ok) {
        failedNames.push(app.applicantName || app.email || app._id);
      }
      await new Promise((resolve) => setTimeout(resolve, 300));
    }
    setDownloadingGroup(null);
    if (failedNames.length > 0) {
      alert(`${failedNames.length} of ${withResumes.length} resume(s) could not be downloaded: ${failedNames.join(', ')}`);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const [appsRes, jobsRes] = await Promise.all([
        api.get('/api/hr/applications'),
        api.get('/api/hr/jobs')
      ]);

      setApplications(appsRes.data.applications || []);
      setJobs(jobsRes.data.jobs || []);
    } catch (err) {
      console.error('Error loading HR applications:', err);
      setError(err.response?.data?.message || 'Failed to load HR applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canAccess) {
      fetchData();
    }
  }, [canAccess]);

  useEffect(() => {
    setReviewNotesDraft(viewingApplication?.reviewNotes || '');
    setNotesError('');
  }, [viewingApplication]);

  useEffect(() => {
    if (!viewingApplication || !focusNotesForApplicationId) return;
    if (String(viewingApplication._id) !== String(focusNotesForApplicationId)) return;

    const timer = setTimeout(() => {
      reviewNotesRef.current?.focus();
      reviewNotesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setFocusNotesForApplicationId(null);
    }, 120);

    return () => clearTimeout(timer);
  }, [viewingApplication, focusNotesForApplicationId]);

  const handleStatusChange = async (applicationId, status, reviewNotes) => {
    try {
      const payload = { status };
      if (reviewNotes !== undefined) {
        payload.reviewNotes = reviewNotes;
      }

      const response = await api.patch(`/api/hr/applications/${applicationId}/status`, payload);
      const updatedApplication = response.data?.application;

      setApplications((prev) => prev.map((app) => (
        app._id === applicationId
          ? {
            ...app,
            ...(updatedApplication || {}),
            status
          }
          : app
      )));

      setViewingApplication((prev) => {
        if (!prev || prev._id !== applicationId) return prev;
        return {
          ...prev,
          ...(updatedApplication || {}),
          status
        };
      });

      return true;
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update application status');
      return false;
    }
  };

  const handleSaveReviewNotes = async () => {
    if (!viewingApplication) return;

    try {
      setSavingNotes(true);
      setNotesError('');
      const ok = await handleStatusChange(
        viewingApplication._id,
        viewingApplication.status,
        reviewNotesDraft
      );

      if (!ok) {
        setNotesError('Could not save notes.');
      }
    } catch (err) {
      setNotesError(err.response?.data?.message || 'Could not save notes.');
    } finally {
      setSavingNotes(false);
    }
  };

  const openApplicationNotes = (application) => {
    setFocusNotesForApplicationId(application._id);
    setViewingApplication(application);
  };

  const handleDeleteApplication = async (applicationId) => {
    if (!window.confirm('Permanently delete this application and its attached resume?')) return;
    try {
      await api.delete(`/api/hr/applications/${applicationId}`);
      setApplications((prev) => prev.filter((app) => app._id !== applicationId));
      setViewingApplication((prev) => (prev?._id === applicationId ? null : prev));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete application');
    }
  };

  const handleArchiveApplication = async (applicationId) => {
    try {
      await api.patch(`/api/hr/applications/${applicationId}/status`, { status: 'archived' });
      setApplications((prev) => prev.map((app) => (
        app._id === applicationId ? { ...app, status: 'archived' } : app
      )));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to archive application');
    }
  };

  const jobsById = useMemo(() => {
    const map = new Map();
    jobs.forEach((job) => map.set(String(job._id), job));
    return map;
  }, [jobs]);

  const visibleApplications = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return applications.filter((application) => {
      if (!showArchived && application.status === 'archived') return false;
      if (selectedJobId !== 'all' && String(application.jobId) !== selectedJobId) return false;
      if (selectedStatus !== 'all' && application.status !== selectedStatus) return false;
      if (!term) return true;

      const haystack = [
        application.applicantName,
        application.email,
        application.phone,
        application.jobTitle,
        application.reviewNotes
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [applications, showArchived, selectedJobId, selectedStatus, searchTerm]);

  const groupedByJob = useMemo(() => {
    const groups = new Map();

    visibleApplications.forEach((application) => {
      const jobKey = String(application.jobId || 'unknown');
      const current = groups.get(jobKey) || {
        jobId: jobKey,
        jobTitle: application.jobTitle || 'Unknown Job',
        items: []
      };

      current.items.push(application);
      groups.set(jobKey, current);
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        items: group.items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      }))
      .sort((a, b) => a.jobTitle.localeCompare(b.jobTitle));
  }, [visibleApplications]);

  const stats = useMemo(() => ({
    total: applications.filter((a) => a.status !== 'archived').length,
    submitted: applications.filter((a) => a.status === 'submitted').length,
    reviewed: applications.filter((a) => a.status === 'reviewed').length,
    approved: applications.filter((a) => a.status === 'approved').length,
    rejected: applications.filter((a) => a.status === 'rejected').length,
    archived: applications.filter((a) => a.status === 'archived').length
  }), [applications]);

  const selectedApplicationJob = viewingApplication
    ? jobsById.get(String(viewingApplication.jobId))
    : null;

  const answerFileEntries = useMemo(() => {
    if (!viewingApplication?.answers || !selectedApplicationJob?.sections) return [];

    const entries = [];

    selectedApplicationJob.sections.forEach((section, sectionIndex) => {
      (section.questions || []).forEach((question, questionIndex) => {
        const key = `${sectionIndex}-${questionIndex}`;
        const answer = viewingApplication.answers[key];

        if (isPotentialFileValue(answer)) {
          entries.push({
            key,
            label: question.label || `Question ${questionIndex + 1}`,
            value: answer
          });
        }
      });
    });

    return entries;
  }, [viewingApplication, selectedApplicationJob]);

  if (!canAccess) {
    return (
      <div className="staff-hr-applications-page">
        <div className="error-message">You do not have HR access.</div>
      </div>
    );
  }

  return (
    <div className="staff-hr-applications-page">
      <div className="applications-page-header">
        <div>
          <h1>HR Applications</h1>
          <p>Review, sort, and manage candidates by the job they applied for.</p>
        </div>
        <div className="applications-page-actions">
          <button className="btn-secondary" onClick={() => navigate('/staff/hr')}>Back to HR Jobs</button>
          <button className="btn-primary" onClick={fetchData}>Refresh</button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="stats-grid">
        <div className="stat-card"><strong>{stats.total}</strong><span>Total Active</span></div>
        <div className="stat-card"><strong>{stats.submitted}</strong><span>Submitted</span></div>
        <div className="stat-card"><strong>{stats.reviewed}</strong><span>In Review</span></div>
        <div className="stat-card"><strong>{stats.approved}</strong><span>Approved</span></div>
        <div className="stat-card"><strong>{stats.rejected}</strong><span>Rejected</span></div>
      </div>

      <section className="filter-bar">
        <div className="filter-group">
          <label htmlFor="job-filter">Job</label>
          <select
            id="job-filter"
            value={selectedJobId}
            onChange={(e) => setSelectedJobId(e.target.value)}
          >
            <option value="all">All Jobs</option>
            {jobs
              .slice()
              .sort((a, b) => a.title.localeCompare(b.title))
              .map((job) => (
                <option key={job._id} value={String(job._id)}>{job.title}</option>
              ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="status-filter">Status</label>
          <select
            id="status-filter"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="all">All Statuses</option>
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group filter-group-search">
          <label htmlFor="app-search">Search</label>
          <input
            id="app-search"
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Applicant name, email, phone..."
          />
        </div>

        <button
          className={`btn-toggle-archived ${showArchived ? 'active' : ''}`}
          onClick={() => setShowArchived((prev) => !prev)}
        >
          {showArchived ? 'Hide Archived' : `Show Archived (${stats.archived})`}
        </button>
      </section>

      {loading ? (
        <div className="empty-state">Loading applications...</div>
      ) : groupedByJob.length === 0 ? (
        <div className="empty-state">No applications found for the current filters.</div>
      ) : (
        <div className="applications-groups">
          {groupedByJob.map((group) => (
            <section key={group.jobId} className="job-group-card">
              <header className="job-group-header">
                <h2>{group.jobTitle}</h2>
                <div className="job-group-header-right">
                  <span>{group.items.length} application{group.items.length === 1 ? '' : 's'}</span>
                  <button
                    type="button"
                    className="btn-sm-outline"
                    onClick={() => exportGroupJson(group)}
                    title="Export applicants as JSON"
                  >
                    Export JSON
                  </button>
                  <button
                    type="button"
                    className="btn-sm-outline"
                    onClick={() => downloadAllResumes(group.jobId, group.items)}
                    disabled={downloadingGroup === group.jobId || !group.items.some((a) => a.resumeUrl)}
                    title="Download all resumes for this job"
                  >
                    {downloadingGroup === group.jobId ? 'Downloading…' : 'Download Resumes'}
                  </button>
                </div>
              </header>

              <div className="job-group-table-wrap">
                <table className="applications-table">
                  <thead>
                    <tr>
                      <th>Applicant</th>
                      <th>Contact</th>
                      <th>Submitted</th>
                      <th>Status</th>
                      <th>Files</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map((application) => (
                      <tr key={application._id} className={application.status === 'archived' ? 'row-archived' : ''}>
                        <td>{application.applicantName}</td>
                        <td>
                          <div>{application.email}</div>
                          {application.phone && <small>{application.phone}</small>}
                        </td>
                        <td>{formatDate(application.createdAt)}</td>
                        <td>
                          <select
                            value={application.status}
                            onChange={(e) => handleStatusChange(application._id, e.target.value)}
                          >
                            {statusOptions.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          {application.resumeUrl ? (
                            <button className="btn-link" onClick={() => openResumeInNewTab(application)}>
                              Open Resume
                            </button>
                          ) : (
                            <span className="muted-text">None</span>
                          )}
                        </td>
                        <td>
                          <div className="row-actions">
                            <button className="btn-link" onClick={() => setViewingApplication(application)}>View</button>
                            <button
                              className="btn-link notes-action"
                              onClick={() => openApplicationNotes(application)}
                              title="Open HR Staff Notes"
                              aria-label={`Open HR Staff Notes for ${application.applicantName}`}
                            >
                              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" className="note-action-icon">
                                <path d="M7 3h10l4 4v14H7z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                                <path d="M17 3v4h4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                                <path d="M10 11h8M10 15h8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                              </svg>
                              Notes
                            </button>
                            {application.status !== 'archived' && (
                              <button className="btn-link muted" onClick={() => handleArchiveApplication(application._id)}>Archive</button>
                            )}
                            <button className="btn-link danger" onClick={() => handleDeleteApplication(application._id)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      )}

      {viewingApplication && (
        <div className="modal-overlay" onClick={() => setViewingApplication(null)}>
          <div className="modal-content app-detail-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3>{viewingApplication.applicantName} - {viewingApplication.jobTitle}</h3>
              <button className="modal-close" onClick={() => setViewingApplication(null)}>x</button>
            </div>

            <div className="modal-body">
              <div className="app-detail-meta">
                <p><strong>Email:</strong> {viewingApplication.email}</p>
                {viewingApplication.phone && <p><strong>Phone:</strong> {viewingApplication.phone}</p>}
                <p><strong>Status:</strong> {statusLabel(viewingApplication.status)}</p>
                <p><strong>Submitted:</strong> {formatDate(viewingApplication.createdAt)}</p>
              </div>

              {viewingApplication.coverLetter && (
                <div className="app-detail-section">
                  <h4>Cover Letter</h4>
                  <p className="pre-wrap">{viewingApplication.coverLetter}</p>
                </div>
              )}

              <div className="app-detail-section">
                <h4>HR Staff Notes</h4>
                <textarea
                  ref={reviewNotesRef}
                  className="review-notes-textarea"
                  value={reviewNotesDraft}
                  onChange={(event) => setReviewNotesDraft(event.target.value)}
                  placeholder="Add private HR review notes for this applicant..."
                />

                <div className="review-notes-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleSaveReviewNotes}
                    disabled={savingNotes}
                  >
                    {savingNotes ? 'Saving…' : 'Save Notes'}
                  </button>
                </div>

                {notesError && <p className="error-inline-text">{notesError}</p>}

                {(viewingApplication.reviewedBy || viewingApplication.reviewedAt) && (
                  <p className="muted-text review-meta-line">
                    Last reviewed by{' '}
                    {viewingApplication.reviewedBy
                      ? `${viewingApplication.reviewedBy.firstName || ''} ${viewingApplication.reviewedBy.lastName || ''}`.trim() || 'Staff'
                      : 'Staff'}
                    {viewingApplication.reviewedAt ? ` on ${formatDate(viewingApplication.reviewedAt)}` : ''}
                  </p>
                )}
              </div>

              <div className="app-detail-section">
                <h4>Attached Files</h4>

                {viewingApplication.resumeUrl ? (
                  <div className="file-item-row">
                    <div>
                      <strong>{viewingApplication.resumeFilename || 'Resume'}</strong>
                      <p className="muted-text">
                        {viewingApplication.resumeFileType || 'Unknown type'}
                        {typeof viewingApplication.resumeSize === 'number'
                          ? ` - ${(viewingApplication.resumeSize / 1024).toFixed(1)} KB`
                          : ''}
                      </p>
                    </div>
                    <div className="file-item-actions">
                      <button className="btn-link" onClick={() => openResumeInNewTab(viewingApplication)}>Open</button>
                      <button className="btn-link" onClick={() => downloadResume(viewingApplication)}>Download</button>
                    </div>
                  </div>
                ) : (
                  <p className="muted-text">No resume attached.</p>
                )}

                {answerFileEntries.length > 0 && (
                  <div className="answer-files-list">
                    <h5>Additional Files from Application Form</h5>
                    {answerFileEntries.map((entry) => (
                      <div key={entry.key} className="file-item-row">
                        <div>
                          <strong>{entry.label}</strong>
                          <p className="muted-text">{entry.value}</p>
                        </div>
                        <div className="file-item-actions">
                          <a className="btn-link" href={getFileUrl(entry.value)} target="_blank" rel="noreferrer">Open</a>
                          <a className="btn-link" href={getFileUrl(entry.value)} download>Download</a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {viewingApplication.answers && Object.keys(viewingApplication.answers).length > 0 && selectedApplicationJob?.sections?.length > 0 && (
                <div className="app-detail-section">
                  <h4>Application Responses</h4>
                  {selectedApplicationJob.sections.map((section, sectionIndex) => (
                    <div key={sectionIndex} className="answers-section-block">
                      <h5>{section.label || `Section ${sectionIndex + 1}`}</h5>
                      {(section.questions || []).map((question, questionIndex) => {
                        const key = `${sectionIndex}-${questionIndex}`;
                        const answer = viewingApplication.answers[key];

                        if (answer === undefined || answer === null || answer === '') return null;

                        return (
                          <div key={questionIndex} className="app-answer-item">
                            <strong>{question.label || `Question ${questionIndex + 1}`}</strong>
                            <p className="pre-wrap">{Array.isArray(answer) ? answer.join(', ') : String(answer)}</p>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffHRApplicationsPage;
